import { ChevronLeftIcon, CloseIcon } from '@velocity/icons/system'
import { ProgressBar, Headline, ActionButton, Text } from '@velocity/ui'
import { TopNavigation } from '@velocity/ui/draft'
import { useCallback, useState } from 'react'
import * as React from 'react'

import {
  useContent,
  useLocalization,
  useStorage,
} from '@ngb-frontend/shared/context'

import { useNavigationBarStyles } from './NavigationBar.styled'
import { FlexBox } from '../FlexBox/FlexBox'
import { FlexColumn } from '../FlexBox/FlexColumn'
import { StopBookingModal } from '../StopBookingModal/StopBookingModal'

import type { AppLocale } from '@ngb-frontend/shared/types'
import type { LocaleSelectorChangeEvent } from '@velocity/ui/draft'

export interface NavigationBarProps {
  label?: string
  topLabel?: string
  onStepBack?: () => void
  onClose?: () => void
  progress?: number
  showLogo?: boolean
  compact?: boolean
  /** Ask whether to stop the booking before closing (defaults to true) */
  confirmClose?: boolean
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  onStepBack,
  onClose,
  label,
  topLabel,
  progress,
  showLogo,
  compact,
  confirmClose = true,
}) => {
  const classes = useNavigationBarStyles({ showLogo })
  const c = useContent()
  const { locale, availableLocales, updateLocale } = useLocalization()
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const { clearLocalStorage } = useStorage()
  const handleOnClose = useCallback(() => {
    clearLocalStorage()
    onClose?.()
  }, [clearLocalStorage, onClose])

  const handleLocaleChange = useCallback(
    (e: LocaleSelectorChangeEvent) => {
      updateLocale(e.value as AppLocale)
    },
    [updateLocale],
  )

  return (
    <FlexBox className={classes.navWrapper}>
      <TopNavigation layoutContainerSize="fluid">
        <>
          {!compact ? (
            <TopNavigation.LocaleSelector
              value={locale}
              localeCodeData={availableLocales}
              onChange={handleLocaleChange}
              data-e2e-component="locale-selector"
            />
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <React.Fragment />
          )}
          <TopNavigation.Logo className={classes.logo} />
        </>
        <TopNavigation.Widgets>
          {onClose && confirmClose && (
            <StopBookingModal
              open={modalIsOpen}
              onContinue={() => setModalIsOpen(false)}
              onStop={handleOnClose}
            />
          )}
          <FlexBox className={classes.root}>
            <ActionButton
              className={classes.button}
              style={{ display: onStepBack ? 'block' : 'none' }}
              data-e2e-component="back-button"
              onClick={() => onStepBack?.()}
              Icon={ChevronLeftIcon}
              title={c('steps.shared.back')}
              aria-label={c('steps.shared.back')}
            />
            {label && (
              <FlexColumn>
                {!compact && topLabel && (
                  <Text variant="100" className={classes.progressText}>
                    {topLabel}
                  </Text>
                )}
                <Headline
                  className={classes.label}
                  variant={compact ? '300' : '100'}
                >
                  {label}
                </Headline>
              </FlexColumn>
            )}
            <ActionButton
              className={classes.button}
              style={{ visibility: onClose ? 'visible' : 'hidden' }}
              data-e2e-component="close-button"
              onClick={
                confirmClose ? () => setModalIsOpen(true) : handleOnClose
              }
              Icon={CloseIcon}
              title={c('steps.shared.close')}
              aria-label={c('steps.shared.close')}
            />
          </FlexBox>
          {!compact && progress !== undefined && (
            <ProgressBar
              className={classes.progressBar}
              aria-label={c('steps.shared.bookingProgress')}
              percent={progress}
              variant="secondary"
            />
          )}
        </TopNavigation.Widgets>
      </TopNavigation>
    </FlexBox>
  )
}
